import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaMinus, FaPlus } from "react-icons/fa6";
import {
  fetchCart,
  updateCartQuantity,
  deleteCartItem,
} from "../redux/actions/cartActions";
import { Link, useNavigate } from "react-router-dom";
import { addToBuyNow } from "../redux/actions/buyNowActions";
import { Modal } from "../components/other";
import { toast } from "react-toastify";
import { checkStockAvailability } from "../redux/actions/stockActions";

const Cart = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { cartItems, loading, error } = useSelector((state) => state.cart);
  const user = useSelector((state) => state.auth?.user);
  const userType = useSelector((state) => state.auth?.user?.user?.user_type);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    dispatch(fetchCart());
  }, [dispatch]);

  const handleIncrement = (item, currentQuantity) => {
    const sub_product_id = item.sub_product?.sub_pro_id;
    const newQuantity = currentQuantity + 1;
    const sp = item.sub_product || {};

    dispatch(checkStockAvailability(sub_product_id, currentQuantity + 1))
      .then((response) => {
        // Log the response
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          // For vendors, show available sets instead of pieces
          const availableItems =
            userType === "vendor"
              ? Math.floor(
                  (data.data?.available_stock || 0) / (sp.no_of_pcs || 1)
                )
              : data.data?.available_stock || 0;

          toast.info("Insufficient Stock !!!");
        } else {
          // Stock is available, update cart quantity
          dispatch(updateCartQuantity(item.cart_id, newQuantity));
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  const handleDecrement = (cart_id, currentQuantity) => {
    if (currentQuantity > 1) {
      dispatch(updateCartQuantity(cart_id, currentQuantity - 1));
    }
  };

  const handleDeleteClick = (cart_id) => {
    setDeleteId(cart_id);
    setIsConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (deleteId !== null) {
      dispatch(deleteCartItem(deleteId));
      setDeleteId(null);
    }
    setIsConfirmOpen(false);
  };

  const items = Array.isArray(cartItems) ? cartItems : [];
  let inStockItems = [];

  const subtotal = items.reduce((sum, item) => {
    if (
      userType === "vendor" &&
      item.available_stock < item.sub_product.no_of_pcs * item.quantity
    ) {
      return sum;
    }

    if (userType === "customer" && item.available_stock < item.quantity) {
      return sum;
    }

    const price =
      userType === "vendor"
        ? item?.sub_product?.vendor_product_price
        : item?.sub_product?.customer_product_price || 0;
    const quantity =
      userType === "vendor"
        ? (item?.quantity || 0) * (item?.sub_product?.no_of_pcs || 1)
        : item?.quantity || 0;
    return sum + price * quantity;
  }, 0);

  if (!user) {
    return (
      <div className="flex flex-col justify-center mt-4 items-center h-64">
        <svg
          className="w-16 h-16 text-purple-400 mb-4"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.5 7.5a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM3.75 21a8.25 8.25 0 1116.5 0H3.75z"
          />
        </svg>
        <div className="text-gray-700 text-xl font-semibold mb-2">
          Your Cart is Waiting!
        </div>
        <div className="text-gray-500 mb-4 text-center max-w-xs">
          Please <span className="font-medium text-purple-600">log in</span> to
          view and manage your cart items, save products, and enjoy a
          personalized shopping experience.
        </div>
        <button
          onClick={() => navigate("/login")}
          className="px-6 py-2 rounded bg-purple-600 text-white font-semibold hover:bg-purple-700 transition"
        >
          Login to Continue
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "75vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading Profile...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 lg:p-4 lg:px-5 min-h-[60vh]">
      {/* Flex container: left = items, right = summary */}
      {items.length === 0 && (
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="bg-white p-4 text-center">
            <h2 className="text-xl font-semibold mb-2">Your Cart is Empty</h2>
            <p className="text-gray-500 mb-4">
              Add items to your cart to proceed.
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-col lg:flex-row lg:gap-6 lg:items-stretch">
        {/* ─── Cart Items: two‑column grid ─── */}
        <div className="flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {items.map((item) => {
              const { product = {}, sub_product: sp = {}, quantity = 0 } = item;
              const vendorUnitPrice = sp?.vendor_product_price ?? 0;
              const customerUnitPrice = sp?.customer_product_price ?? 0;
              const unitPrice =
                userType === "vendor" ? vendorUnitPrice : customerUnitPrice;
              const lineTotal = unitPrice * quantity;

              if (
                userType === "vendor"
                  ? item?.available_stock >= sp.no_of_pcs * quantity
                  : item?.available_stock >= quantity
              ) {
                inStockItems = [...inStockItems, item];
              }
              (userType === "vendor"
                ? item?.available_stock < sp.no_of_pcs * quantity
                : item?.available_stock < quantity) && (
                <p className="italic text-sm mt-2 text-red-600 font-semibold whitespace-nowrap">
                  {/* {setOutOfStockLength((prev) => prev + 1)} */}
                  Out Of Stock
                </p>
              );

              return (
                <div
                  key={item.cart_id}
                  className="shadow overflow-hidden p-3 rounded-xl flex gap-3 items-center"
                >
                  <div className="flex-shrink-0 h-[25vh] lg:h-[25vh] aspect-[3/4] rounded-xl overflow-hidden">
                    <img
                      loading="lazy"
                      src={
                        sp.sub_pro_img ||
                        product.product_primary_img ||
                        "/placeholder.jpg"
                      }
                      alt={product.product_name || "Product"}
                      className="size-full"
                    />
                  </div>
                  <div className="flex flex-col flex-1 gap-1">
                    <h5 className="fw-bold text-base lg:text-xl truncate line-clamp-1 whitespace-normal">
                      {product.product_name || "Unnamed Product"}
                    </h5>
                    <p className="text-gray-500 text-sm lg:text-base">
                      Color: {sp.color || "N/A"}
                    </p>
                    <p className="text-gray-500 text-sm lg:text-base">
                      Size: {sp.size || "N/A"}
                    </p>
                    <p className="fw-bold text-base lg:text-xl mt-2">
                      {/* ₹{lineTotal.toFixed(2)} */}₹
                      {userType === "vendor"
                        ? sp.set_price *
                          (quantities[sp?.sub_pro_id] || 1) *
                          quantity
                        : customerUnitPrice * (quantities[sp?.sub_pro_id] || 1)}
                    </p>
                    <div className="flex flex-col lg:flex-row gap-2 mt-2">
                      <div className="flex gap-0 mt-2">
                        <button
                          onClick={() =>
                            quantity <= 1
                              ? handleDeleteClick(item.cart_id)
                              : handleDecrement(item.cart_id, quantity)
                          }
                          className="px-2 lg:px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-s-2xl"
                        >
                          <FaMinus />
                        </button>
                        <p className="px-2 py-2 bg-gray-200">{quantity}</p>
                        <button
                          disabled={
                            userType === "vendor"
                              ? item?.available_stock <= sp.no_of_pcs * quantity
                              : item?.available_stock <= quantity
                          }
                          onClick={() => handleIncrement(item, quantity)}
                          className="px-2 lg:px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-e-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <FaPlus />
                        </button>
                      </div>
                      <button
                        onClick={() => handleDeleteClick(item.cart_id)}
                        className="text-red-600 text-base px-2 lg:text-xl lg:mt-2 rounded-lg hover:bg-red-500 hover:text-white"
                        aria-label={`Delete ${product.product_name} from wishlist`}
                      >
                        <RiDeleteBin6Line className="size-5" />
                      </button>
                    </div>
                    {(userType === "vendor"
                      ? item?.available_stock < sp.no_of_pcs * quantity
                      : item?.available_stock < quantity) && (
                      <p className="italic text-sm mt-2 text-red-600 font-semibold whitespace-nowrap">
                        {/* {setOutOfStockLength((prev) => prev + 1)} */}
                        Out Of Stock
                      </p>
                    )}
                    {/* Note Text */}
                    {userType === "vendor" &&
                    sp.selling_type == "set" &&
                    sp.no_of_pcs ? (
                      <p className="italic text-sm text-red-600 font-semibold">
                        {` ${quantities[sp.sub_pro_id] || 1} Set = ${
                          sp.no_of_pcs * (quantities[sp.sub_pro_id] || 1)
                        } pieces`}{" "}
                        {/* (₹{sp.set_price * (quantities[sp?.sub_pro_id] || 1)}) */}
                        (₹{unitPrice.toFixed(2)} per piece)
                      </p>
                    ) : (
                      // Invisible div to take up the same space so button stays right-aligned
                      <div className="min-w-[180px]" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── Summary: sticky in third column on lg+ ─── */}
        {items.length > 0 && (
          <div className="lg:w-1/3 mt-2 lg:mt-0 lg:border-l-2 lg:border-gray-200 lg:pl-6">
            <div className="sticky">
              <div className="bg-white border p-4 shadow-md">
                <p className="text-lg font-semibold">Subtotal:</p>
                <p className="text-xl font-bold text-green-600 mb-4">
                  ₹{subtotal.toFixed(2)}
                </p>

                <button
                  className="w-full block text-center px-6 py-3 bg-green-600 disabled:bg-gray-300 text-white rounded-full font-semibold hover:bg-green-700 transition-all"
                  disabled={!inStockItems.length}
                  onClick={(e) => {
                    if (!inStockItems.length) {
                      return;
                    }

                    dispatch(addToBuyNow(inStockItems));
                    navigate("/checkout");
                  }}
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Delete Confirmation Modal ─── */}
      <Modal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title="Remove from Cart?"
      >
        <p className="mb-6">Are you sure you want to delete this item?</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setIsConfirmOpen(false)}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Cart;
